export type CoordinationConfig = {
  readonly enabled: boolean;
  readonly channelName?: string;
};

export type CoordinationDebugState = {
  readonly enabled: boolean;
  readonly tabId: string;
  readonly isLeader: boolean;
  readonly leaderTabId: string | null;
  readonly tabCount: number;
  readonly transport: 'broadcastChannel' | 'storageEvents' | 'none';
};

type LeaderMessage =
  | { type: 'leader_claim'; tabId: string; at: number }
  | { type: 'leader_release'; tabId: string }
  | { type: 'heartbeat'; tabId: string; at: number };

export class CoordinationManager {
  private readonly config: CoordinationConfig;

  private readonly tabId: string;

  private readonly channelName: string;

  private channel: BroadcastChannel | null = null;

  private storageKey: string;

  private isLeader = false;

  private leaderTabId: string | null = null;

  private tabCount = 1;

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  private onLeaderChange: ((isLeader: boolean) => void) | null = null;

  private storageListener: ((event: StorageEvent) => void) | null = null;

  private transport: CoordinationDebugState['transport'] = 'none';

  constructor(config: CoordinationConfig, instanceId: string) {
    this.config = config;
    this.tabId = `tab_${instanceId}_${crypto.randomUUID().slice(0, 8)}`;
    this.channelName =
      config.channelName ?? `pixel_transport_coordination_${instanceId}`;
    this.storageKey = `${this.channelName}:leader`;
  }

  init(onLeaderChange?: (isLeader: boolean) => void): void {
    if (!this.config.enabled || typeof window === 'undefined') {
      this.isLeader = true;
      this.leaderTabId = this.tabId;
      return;
    }

    this.onLeaderChange = onLeaderChange ?? null;

    if (typeof BroadcastChannel !== 'undefined') {
      this.transport = 'broadcastChannel';
      this.channel = new BroadcastChannel(this.channelName);
      this.channel.onmessage = event =>
        this.handleMessage(event.data as LeaderMessage);
    } else {
      this.transport = 'storageEvents';
      this.storageListener = (event: StorageEvent) => {
        if (event.key !== this.storageKey || !event.newValue) return;
        try {
          this.handleMessage(JSON.parse(event.newValue) as LeaderMessage);
        } catch {
          // ignore malformed messages
        }
      };
      window.addEventListener('storage', this.storageListener);
    }

    this.claimLeadership();
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), 2000);
  }

  destroy(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.isLeader) {
      this.releaseLeadership();
    }

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    if (this.storageListener && typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }

    this.onLeaderChange = null;
  }

  canSend(): boolean {
    if (!this.config.enabled) return true;
    return this.isLeader;
  }

  shouldPersistOnly(): boolean {
    return this.config.enabled && !this.isLeader;
  }

  isTabLeader(): boolean {
    return this.isLeader;
  }

  getDebugState(): CoordinationDebugState {
    return {
      enabled: this.config.enabled,
      tabId: this.tabId,
      isLeader: this.isLeader,
      leaderTabId: this.leaderTabId,
      tabCount: this.tabCount,
      transport: this.transport,
    };
  }

  private claimLeadership(): void {
    const message: LeaderMessage = {
      type: 'leader_claim',
      tabId: this.tabId,
      at: Date.now(),
    };
    this.broadcast(message);

    if (!this.leaderTabId) {
      this.setLeader(this.tabId);
    }
  }

  private releaseLeadership(): void {
    const message: LeaderMessage = {
      type: 'leader_release',
      tabId: this.tabId,
    };
    this.broadcast(message);
    if (this.isLeader) {
      this.setLeader(null);
    }
  }

  private sendHeartbeat(): void {
    if (!this.isLeader) return;
    this.broadcast({ type: 'heartbeat', tabId: this.tabId, at: Date.now() });
  }

  private handleMessage(message: LeaderMessage): void {
    switch (message.type) {
      case 'leader_claim':
        if (!this.leaderTabId || message.at < Date.now()) {
          this.setLeader(message.tabId);
        }
        break;
      case 'leader_release':
        if (this.leaderTabId === message.tabId) {
          this.setLeader(null);
          if (!this.isLeader) {
            this.claimLeadership();
          }
        }
        break;
      case 'heartbeat':
        this.tabCount = Math.max(this.tabCount, 1);
        if (this.leaderTabId === message.tabId) {
          this.leaderTabId = message.tabId;
        }
        break;
      default:
        break;
    }
  }

  private setLeader(tabId: string | null): void {
    const wasLeader = this.isLeader;
    this.leaderTabId = tabId;
    this.isLeader = tabId === this.tabId;

    if (wasLeader !== this.isLeader) {
      this.onLeaderChange?.(this.isLeader);
    }
  }

  private broadcast(message: LeaderMessage): void {
    if (this.channel) {
      this.channel.postMessage(message);
      return;
    }

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(message));
      } catch {
        // ignore quota errors
      }
    }
  }
}
